import { Component, OnInit, Input, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatStepperModule } from '@angular/material/stepper';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { MatDividerModule } from '@angular/material/divider';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { Application, ApplicationArea, ApplicationCategory } from '../../../../common/models/application';
import { FileInfo } from '../../../../common/models/file';
import { ApplicationService } from '../../../../common/services/application.service';
import { FileService } from '../../../../common/services/file.service';
import { Guid } from 'guid-typescript';
import { CostType } from '../../../../common/models/common';
import { BudgetItem } from '../../../../common/models/submission';
import { Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'app-application-view',
  templateUrl: './application-view.component.html',
  styleUrls: ['./application-view.component.scss'],
  imports: [
    CommonModule,
    MatStepperModule,
    MatCardModule,
    MatIconModule,
    MatButtonModule,
    MatDividerModule,
    RouterModule
]
})
export class ApplicationViewComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  
  isHandset: boolean = false;

  @Input() application!: Application;

  applicationFiles: FileInfo[] = [];
  budgetItemFileMap: { [budgetItemId: string]: FileInfo[] } = {};

  // ─── Updated Field Definitions with a "type" Property ──────────────────────────────
  generalInfoFields = [
    { label: 'Naziv organizacije', property: 'organizationName', type: 'text' },
    { label: 'Adresa (ulica i broj)', property: 'address', type: 'text' },
    { label: 'Poštanski broj i sjedište', property: 'postalCodeAndHeadquarters', type: 'text' },
    { label: 'Županija', property: 'county', type: 'text' },
    { label: 'Ime i prezime osobe ovlaštene za zastupanje', property: 'authorizedRepresentative', type: 'text' },
    { label: 'Adresa e-pošte osobe ovlaštene za zastupanje', property: 'authorizedRepresentativeEmail', type: 'text' },
    { label: 'Dužnost osobe ovlaštene za zastupanje', property: 'authorizedRepresentativeRole', type: 'text' },
    { label: 'Telefon', property: 'telephone', type: 'text' },
    { label: 'Mobitel', property: 'mobilePhone', type: 'text' },
    { label: 'Telefaks', property: 'fax', type: 'text' },
    { label: 'Adresa e-pošte', property: 'organizationEmail', type: 'text' },
    { label: 'Internetska stranica', property: 'website', type: 'text' },
    { label: 'Godina osnutka', property: 'yearOfEstablishment', type: 'number' },
    { label: 'Datum i godina upisa u matični registar', property: 'dateOfRegistration', type: 'date' },
    { label: 'Registarski broj', property: 'registrationNumber', type: 'text' },
    { label: 'Registrirana pri (naziv registracijskog tijela)', property: 'registeredAuthority', type: 'text' },
    { label: 'Naziv banke', property: 'bankName', type: 'text' },
    { label: 'Broj žiro-računa (IBAN)', property: 'bankAccountNumber', type: 'text' },
    { label: 'OIB', property: 'oib', type: 'text' },
    { label: 'RNO', property: 'rno', type: 'text' },
    { label: 'Ciljevi osnivanja sukladno Statutu', property: 'objectivesAccordingToStatute', type: 'text' },
    { label: 'Svrha i područje djelovanja', property: 'purposeAndFieldOfOperation', type: 'text' },
    { label: 'Djelatnost(i) organizacije sukladno Statutu', property: 'activitiesAccordingToStatute', type: 'text' },
    { label: 'Ukupan broj članova', property: 'totalMembers', type: 'number' },
    { label: 'Ukupan broj građana', property: 'totalIndividualMembers', type: 'number' },
    { label: 'Ukupan broj pravnih osoba', property: 'totalLegalEntities', type: 'number' },
    { label: 'Udio volonterskog rada u organizaciji ( % )', property: 'volunteerWorkShare', type: 'number' },
    { label: 'Broj osoba koje volontiraju', property: 'numberOfVolunteers', type: 'number' },
    { label: 'Broj sati volonterskog rada ostvarenih u prethodnoj godini', property: 'volunteerHoursInPreviousYear', type: 'number' },
    { label: 'Broj zaposlenih na određeno u prethodnoj godini', property: 'temporaryEmployees', type: 'number' },
    { label: 'Broj zaposlenih na neodređeno u prethodnoj godini', property: 'permanentEmployees', type: 'number' },
    { label: 'Je li organizacija u sustavu PDV-a', property: 'isInVATSystem', type: 'bool' },
    { label: 'Ukupno ostvareni prihodi u prethodnoj godini', property: 'totalIncomeInPreviousYear', type: 'currency' },
    { label: 'Prihodi ostvareni od donacija državnog proračuna u prethodnoj godini', property: 'incomeFromGovernmentDonationsInPreviousYear', type: 'currency' },
    { label: 'Prihodi ostvareni od donacija iz proračuna jedinica lokane i područne (regionalne) samouprave u prethodnoj godini', property: 'incomeFromLocalAndRegionalAuthoritiesInPreviousYear', type: 'currency' },
    { label: 'Prihodi ostvareni od inozemnih vlada i međunarodnih organizacija u prethodnoj godini', property: 'incomeFromInternationalOrganizationsInPreviousYear', type: 'currency' },
    { label: 'Prihodi ostvareni od trgovačkih društava i ostalih pravnih osoba u prethodnoj godini', property: 'incomeFromBusinessesAndLegalEntitiesInPreviousYear', type: 'currency' },
    { label: 'Prihodi ostvareni od građana i kućanstava u prethodnoj godini', property: 'incomeFromIndividualsInPreviousYear', type: 'currency' },
    { label: 'Prihodi ostvareni od povezanih neprofitnih organizacija u prethodnoj godini', property: 'incomeFromNonprofitOrganizationsInPreviousYear', type: 'currency' },
    { label: 'Prihodi ostvareni od prihoda od članarine u prethodnoj godini', property: 'membershipFeesInPreviousYear', type: 'currency' },
    { label: 'Prihodi ostvareni iz EU fondova u prethodnoj godini', property: 'euFundsInPreviousYear', type: 'currency' },
    { label: 'Ukupan iznos isplaćen za plaće u prethodnoj godini', property: 'totalSalaryPaidInPreviousYear', type: 'currency' },
    { label: 'Ukupan iznos isplaćen za naknade drugog dohotka u prethodnoj godini', property: 'totalCompensationPaidInPreviousYear', type: 'currency' },
    { label: 'Vlastiti prostor (veličina u kvadratnim metrima)', property: 'ownSpaceSize', type: 'number' },
    { label: 'Iznajmljeni prostor (veličina prostora u kvadratnim metrima)', property: 'rentedSpaceSize', type: 'number' },
    { label: 'prostor općine/grada/županije/RH (veličina prostora u kvadratnim metrima)', property: 'municipalSpaceSize', type: 'number' },
    { label: 'Iznos mjesečnog najama za prostor općine/grada/županije/RH', property: 'monthlyRentForMunicipalSpace', type: 'currency' },
    { label: 'Broj ukupno odobrenih bespovratnih potpora u prethodnoj godini', property: 'totalApprovedGrantsInPreviousYear', type: 'number' },
    { label: 'Broj partnerstva u koja je organizacija uključena na provedbi programa/projekta u trenutku prijave na ovaj natječaj', property: 'numberOfPartnerships', type: 'number' },
    { label: 'Izrađujete li godišnji izvještaj o radu?', property: 'isAnnualReportPrepared', type: 'bool' },
    { label: 'Ukoliko ste označili odgovor “da”, kome ga dostavljate i na koji način ga predstavljate javnosti?', property: 'reportRecipients', type: 'text' },
    { label: 'Provodite li neki od sustava kvalitete za neprofitne organizacije?', property: 'implementsQualitySystem', type: 'bool' },
    { label: 'Ukoliko ste označili odgovor da, koji sustav i od kada?', property: 'qualitySystemDetails', type: 'text' }
  ];

  programProjectFields = [
    { label: 'Kategorija (program/projekt)', property: 'category', type: 'text' },
    { label: 'Područje djelovanja na koje se program/projekt odnosi', property: 'area', type: 'text' },
    { label: 'Naziv programa/projekta', property: 'programProjectName', type: 'text' },
    { label: 'Sažetak programa/projekta', property: 'summary', type: 'text' },
    { label: 'Predviđeno trajanje provedbe u mjesecima', property: 'expectedDurationMonths', type: 'number' },
    { label: 'Zemljopisno područje provedbe (mjesto, grad, općina, županija, država)', property: 'geographicalArea', type: 'text' },
    { label: 'Ukupan iznos potreban za provedbu', property: 'totalAmountRequired', type: 'currency' },
    { label: 'Iznos koji se traži od Općine (do 100% ukupne vrijednosti programa/projekta)', property: 'amountRequestedFromMunicipality', type: 'currency' },
    { label: 'Ako je iznos ukupnih troškova za provedbu programa/projekta veći od iznosa koji se može dobiti od Općine Donji Andrijevci, molimo navedite kako ćete osigurati ostatak sredstava za provedbu akcije', property: 'additionalFundingSources', type: 'text' },
    { label: 'Opišite što Vas je potaknulo da prijavite ovaj program/projekt, odnosno kako ste utvrdili potrebe i koji problem rješavate ovim programom/projektom', property: 'motivationDescription', type: 'text' },
    { label: 'Navedite i opišite ciljeve koji se namjeravaju ostvariti provedbom predloženog programa/projekta', property: 'projectGoals', type: 'text' },
    { label: 'Opišite mjerljive rezultate koje očekujete po završetku provođenja vašeg programa/projekta', property: 'expectedResults', type: 'text' },
    { label: 'Objasnite na koji način i kojim sadržajima predloženi program/projekt doprinosi ostvarenju općeg i posebnih ciljeva utvrđenih pozivom ili natječajem', property: 'contributionToGoals', type: 'text' },
    { label: 'Tko su ciljane skupine (skupine na koje aktivnosti izravno utječu) obuhvaćene projektom, njihov broj i struktura?', property: 'targetGroups', type: 'text' },
    { label: 'Tko su krajnji korisnici (pojedinci, skupine, organizacije koje nisu izravno uključene u provedbu, ali provedba na njih ima posredan utjecaj)? Na koji način će na njih utjecati?', property: 'endUsersImpact', type: 'text' },
    { label: 'Voditeljica / voditelj programa/projekta (upišite ime i prezime i priložite životopis na propisanom obrascu ukoliko je životopis kao prilog obvezan sukladno Uputama za prijavitelje)', property: 'projectLeader', type: 'text' },
    { label: 'Broj volontera koji sudjeluju u provedbi programa/projekta (navedite broj volontera, broj predviđenih volonterskih sati i zaduženja)', property: 'numberOfVolunteersInvolved', type: 'number' },
    { label: 'Broj zaposlenih osoba koje sudjeluju u provedbi programa/projekta', property: 'numberOfEmployeesInvolved', type: 'number' },
    { label: 'Jeste li do sada imali iskustva u organiziranju i provedbi sličnih programa/projekata? Ako jeste, molimo da to ovdje kratko opišete', property: 'similarExperience', type: 'text' }
  ];

  visibilityFields = [
    { label: 'Opišite na koji način planirate uključiti stanovnike Općine u aktivnosti programa/projekta te informirati širu javnost o tijeku provedbe i rezultatima programa/projekta', property: 'publicEngagementPlan', type: 'text' }
  ];

  // Processed field arrays (including type) used in the template
  generalInfoFieldValues: Array<{ label: string; property: string; value: any; type: string }> = [];
  programProjectFieldValues: Array<{ label: string; property: string; value: any; type: string }> = [];
  visibilityFieldValues: Array<{ label: string; property: string; value: any; type: string }> = [];

  constructor(
    private applicationService: ApplicationService, 
    private route: ActivatedRoute, 
    private fileService: FileService,
    private breakpointObserver: BreakpointObserver
  ) {}

  ngOnInit(): void {
    const applicationIdParamName = 'applicationId';
    this.route.paramMap.pipe(takeUntil(this.destroy$)).subscribe((params) => {
      if (params.get(applicationIdParamName)) {
        const applicationIdParam = params.get(applicationIdParamName);
        if (!applicationIdParam) {
          return;
        }
        const applicationId = Guid.parse(applicationIdParam);
        this.applicationService.getApplication(applicationId).pipe(takeUntil(this.destroy$)).subscribe((application) => {
          this.application = application;    
          this.processFields();  
          this.loadBudgetItemAttachments();  
          this.loadApplicationAttachments(); 
        });
      }
    });

    this.breakpointObserver.observe([Breakpoints.XSmall, Breakpoints.Small]).pipe(takeUntil(this.destroy$))
    .subscribe(result => {
      this.isHandset = result.matches;
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  processFields(): void {
    this.generalInfoFieldValues = this.generalInfoFields.map(field => ({
      label: field.label,
      property: field.property,
      value: (this.application as any)[field.property],
      type: field.type
    }));

    this.programProjectFieldValues = this.programProjectFields.map(field => ({
      label: field.label,
      property: field.property,
      value: (this.application as any)[field.property],
      type: field.type
    }));

    this.visibilityFieldValues = this.visibilityFields.map(field => ({
      label: field.label,
      property: field.property,
      value: (this.application as any)[field.property],
      type: field.type
    }));
  }

  get directBudgetItems(): BudgetItem[] {
    return this.application?.budgetPlan?.budgetItems?.filter(
      item => item.type === CostType.Direct
    ) || [];
  }

  get indirectBudgetItems(): BudgetItem[] {
    return this.application?.budgetPlan?.budgetItems?.filter(
      item => item.type === CostType.Indirect
    ) || [];
  }

  private loadApplicationAttachments(): void {
    if (!this.application?.fileStorageLocation) {
      return;
    }
    this.fileService.listDocuments(this.application.fileStorageLocation).pipe(takeUntil(this.destroy$)).subscribe({
      next: (files) => {
        this.applicationFiles = files;
      },
      error: (err) => {
        console.error('Error loading application attachments', err);
      }
    });
  }

  private loadBudgetItemAttachments(): void {
    if (!this.application?.budgetPlan?.budgetItems) {
      return;
    }

    this.application.budgetPlan.budgetItems.forEach((item) => {
      if (!item.fileStorageLocation) {
        return;
      }

      this.fileService.listDocuments(item.fileStorageLocation).pipe(takeUntil(this.destroy$)).subscribe({
        next: (files) => {
          this.budgetItemFileMap[item.id.toString()] = files;
        },
        error: (err) => {
          console.error('Error loading budget item attachments for item:', item.id, err);
        },
      });
    });
  }

  downloadFile(file: FileInfo): void {
    this.fileService.downloadDocument(file.link).pipe(takeUntil(this.destroy$)).subscribe({
      next: (blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = file.name;
        a.click();
        window.URL.revokeObjectURL(url);
      },
      error: (err) => {
        console.error('Error downloading document', err);
      }
    });
  }

  getCategoryLabel(category: ApplicationCategory | undefined): string {
    switch (category) {
      case ApplicationCategory.Program:
        return 'Program';
      case ApplicationCategory.Project:
        return 'Projekt';
      case ApplicationCategory.Manifestation:
        return 'Manifestacija';
      default:
        return 'Nepoznata kategorija';
    }
  }

  getAreaLabel(area: ApplicationArea | undefined): string {
    switch (area) {
      case ApplicationArea.SportAndRecreation:
        return 'I. Sport i rekreacija';
      case ApplicationArea.Culture:
        return 'II. Kultura';
      case ApplicationArea.OtherSocialActivities:
        return 'III. Ostale društvene djelatnosti';
      default:
        return 'Nepoznato područje';
    }
  }

  getFundingSourceTypeLabel(type: number): string {
    switch (type) {
      case 1:
        return 'I. Vlastiti izvori';
      case 2:
        return 'II. Ostala tijela javne vlasti';
      case 3:
        return 'III. Jedinice lokalne i područne (regionalne) samouprave';
      case 4:
        return 'IV. Drugo';
      default:
        return 'Nepoznat izvor';
    }
  }
}